import React, { useState, useEffect, useRef } from 'react';
import { Crown } from 'lucide-react';
import { t } from '../../i18n';
import { nextStageLabel } from '../../i18n/errors';
import Modal from '../ui/Modal';
import { formatRoundAction, getSnapCents } from '../../utils/currency';
import { useCurrency, useFormatCurrency } from '../../utils/currencyContext';
import { getMaximumRaise } from '../../utils/pokerLogic';
import { isAway, isFoldedThisHand, isViewerHost, isWaitingNextHand, seatPresence } from '../../utils/playerLedger';
import { PendingRebuyRows, RebuyAskForm } from './RebuyAskCard';
import { streetActionLabel } from '../../utils/streetAction';
import { handOverStatusTail } from '../../utils/winMessages';
import { SIT_OUT_COPY, seatStatusLabel } from '../../utils/sitOutCopy';
import { useVisualViewport } from '../../utils/visualViewport';

// Inline after the host's name. currentColor so it is ink on cream seats
// and cream on a black (active) seat. 0.85em so it tracks the name size.
function HostCrown() {
  return (
    <Crown
      data-testid="host-crown"
      className="w-[0.85em] h-[0.85em] flex-shrink-0"
      aria-label={t('common.host')}
    />
  );
}

// Presentational pieces shared by BOTH game-screen layouts (list and table).
// Moved here verbatim from GameBoard.jsx: the container still owns every piece
// of state, every handler and every modal, so the two layouts can never drift
// apart on behaviour — they render the same components over a different frame.

// Highest current-round bet across a players object (Firebase map form).
function highestBet(players) {
  return Math.max(0, ...Object.values(players || {}).map(p => p.currentRoundBet || 0));
}

// Single status banner: always answers "whose move is it, and what's next".
// On the viewer's turn it inverts to the ink fill and pulses; every other
// state renders on the quiet cream banner. Dots are monochrome by design —
// danger and gold are reserved accents and never used as status decoration.
function GameStatusBanner({ game, players, viewerId, hostId, tapWinnerMode = false }) {
  const formatCurrency = useFormatCurrency();
  const me = players[viewerId];
  const isHost = isViewerHost(viewerId, hostId, players);
  const presence = seatPresence(me);
  const waitingNext = presence === 'next';
  const turnPlayer = players[game?.currentTurnPlayerId];
  const turnIsLive = !!(turnPlayer && !isWaitingNextHand(turnPlayer) && !isAway(turnPlayer));
  const myTurn = presence === 'live' && turnIsLive && game?.currentTurnPlayerId === viewerId;
  const toCall = me ? Math.max(0, highestBet(players) - (me.currentRoundBet || 0)) : 0;
  const postedMissed = me?.missedBlindPostCents || 0;

  const STAGE = {
    PRE_FLOP: t('game.stage.preFlop'),
    FLOP: t('game.stage.flop'),
    TURN: t('game.stage.turn'),
    RIVER: t('game.stage.river'),
    SHOWDOWN: t('game.stage.showdown'),
  };
  const NEXT = {
    PRE_FLOP: t('game.next.preFlop'),
    FLOP: t('game.next.flop'),
    TURN: t('game.next.turn'),
    RIVER: t('game.next.river'),
  };
  const stage = game?.stage || 'PRE_FLOP';
  const stageLabel = STAGE[stage] || stage;

  // Every clause after a bullet reads as its own sentence, so each one is
  // capitalized on its own rather than continuing the previous clause's case.
  let lead, tail;
  if (game?.winnerInfo) {
    lead = t('game.handOver');
    tail = handOverStatusTail(game.winnerInfo, players, formatCurrency)
      || (isHost ? t('game.dealNextWhenReady') : t('game.waitingOnHost'));
  } else if (postedMissed > 0 && presence !== 'away' && presence !== 'pending') {
    lead = myTurn ? t('game.yourTurn') : stageLabel;
    tail = SIT_OUT_COPY.postingMissedBlinds(formatCurrency(postedMissed));
  } else if (presence === 'away') {
    lead = stageLabel;
    tail = SIT_OUT_COPY.statusTail;
  } else if (presence === 'pending') {
    lead = stageLabel;
    tail = SIT_OUT_COPY.statusTailPending;
  } else if (waitingNext) {
    lead = stageLabel;
    tail = t('game.youreInNext');
  } else if (myTurn) {
    lead = t('game.yourTurn');
    tail = toCall > 0
      ? `${stageLabel} • ${t('game.toCall', { amount: formatCurrency(toCall) })}`
      : `${stageLabel} • ${t('game.youCanCheck')}`;
  } else if (turnIsLive) {
    lead = stageLabel;
    tail = t('game.waitingOnAct', { name: turnPlayer.name });
  } else if (game?.canAdvanceStage) {
    lead = t('game.complete', { stage: stageLabel });
    tail = NEXT[stage] || t('game.continueHand');
  } else {
    lead = t('game.stage.showdown');
    // Kept short so the banner stays on ONE line at 360pt — its height is
    // locked (see below), so a long tail would only be cut off.
    tail = isHost
      ? (tapWinnerMode ? t('game.tapWinnerSeat') : t('game.declareWinner'))
      : t('game.hostDeclares');
  }

  const active = myTurn && !game?.winnerInfo;
  return (
    <div
      data-testid="game-status"
      // Fixed one-line height (never wraps): the banner must not change size
      // between states, or everything below it would shift.
      className={`mt-3 h-[36px] min-w-0 flex items-center justify-center gap-2 rounded-[10px] px-3.5 border overflow-hidden ${
      active ? 'bg-ink border-ink text-cream' : 'bg-cream-2 border-line text-ink-2'
    }`}>
      <span className={`w-[7px] h-[7px] rounded-full shrink-0 ${active ? 'bg-cream animate-pulse' : 'bg-mute'}`} />
      <span
        className="text-[13px] leading-[18px] whitespace-nowrap overflow-hidden text-ellipsis min-w-0 max-w-full"
        title={`${lead} • ${tail}`}
      >
        <span className="font-bold">{lead}</span>
        <span className={active ? 'opacity-50' : 'opacity-45'}> • </span>
        {tail}
      </span>
    </div>
  );
}

// Current-round action list. Opened from the game Menu — the last-action
// line no longer sits on the board (seat cards already show the street action).
function HistoryModal({ open, onClose, actions }) {
  const formatCurrency = useFormatCurrency();
  const list = actions || [];

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('game.history')}
      compact
      testId="history"
    >
      {list.length === 0 ? (
        <p className="text-[13.5px] text-mute m-0">{t('game.historyEmpty')}</p>
      ) : (
        <ul className="space-y-2.5">
          {list.map((a, i) => {
            const isLatest = i === list.length - 1;
            return (
              <li
                key={`${a.ts}-${i}`}
                className={`text-[13.5px] tnum leading-snug ${isLatest ? 'text-ink font-medium' : 'text-mute'}`}
              >
                {formatRoundAction(a, formatCurrency)}
              </li>
            );
          })}
        </ul>
      )}
    </Modal>
  );
}

// Dismissal key for a winnerInfo broadcast. Includes the callout timestamp so
// that a callout arriving after this client tapped "Got it" produces a NEW key
// — the dismissal no longer matches and the modal re-opens showing the callout.
function winnerInfoDismissKey(winnerInfo) {
  return `${winnerInfo.handNumber}-${winnerInfo.ts}-${winnerInfo.callout?.ts ?? 0}`;
}

// Animate an integer (cents) from 0 → target over `duration` ms.
function useCountUp(target, duration = 900) {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (!target || target <= 0) {
      setValue(0);
      return;
    }
    let raf;
    const start = performance.now();
    const tick = (now) => {
      const t = Math.min(1, (now - start) / duration);
      // easeOutCubic
      const eased = 1 - Math.pow(1 - t, 3);
      setValue(Math.round(target * eased));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, duration]);
  return value;
}

// Confetti for the winner's celebration — 18 cream dots/rings drifting up.
function ConfettiField() {
  const pieces = React.useMemo(() => (
    Array.from({ length: 18 }, (_, i) => {
      const ring = i % 3 === 0;
      const size = 6 + (i % 4) * 2;
      return {
        id: i,
        left: `${(i * 53) % 100}%`,
        top: `${20 + ((i * 37) % 60)}%`,
        size,
        ring,
        drift: `${((i % 5) - 2) * 26}px`,
        rise: `${-120 - ((i * 17) % 110)}px`,
        delay: `${(i % 6) * 90}ms`,
        duration: `${1400 + ((i * 53) % 700)}ms`,
      };
    })
  ), []);

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {pieces.map(p => (
        <span
          key={p.id}
          style={{
            position: 'absolute',
            left: p.left,
            top: p.top,
            width: p.size,
            height: p.size,
            borderRadius: '50%',
            background: p.ring ? 'transparent' : 'rgba(246,244,239,0.92)',
            border: p.ring ? '1.5px solid rgba(246,244,239,0.85)' : 'none',
            '--drift': p.drift,
            '--rise': p.rise,
            animation: `cl-win-mark ${p.duration} ease-out ${p.delay} infinite`,
          }}
        />
      ))}
    </div>
  );
}

// Table Vibe headline — bold "You"-anchored prefix, muted remainder. Every
// template starts with the winner name(s), so the split is safe; if a message
// ever doesn't, the whole line renders bold as a fallback.
function WinHeadline({ message, boldPart }) {
  const canSplit = boldPart && message.startsWith(boldPart);
  return (
    <h2 className="text-[26px] leading-[1.1] tracking-[-0.02em] m-0">
      {canSplit ? (
        <>
          <span style={{ fontWeight: 600, color: '#0d0d0d' }}>{boldPart}</span>
          <span style={{ fontWeight: 400, color: '#5c574e' }}>{message.slice(boldPart.length)}</span>
        </>
      ) : (
        <span style={{ fontWeight: 600, color: '#0d0d0d' }}>{message}</span>
      )}
    </h2>
  );
}

// Callout line — secondary, beneath the main win message on every device.
function CalloutLine({ text }) {
  if (!text) return null;
  return (
    <p className="mt-3 text-[15px] leading-snug m-0" style={{ color: '#5c574e' }}>
      {text}
    </p>
  );
}

// Celebration modal — shown to the winning player (non-host). The host sees
// HostHandResult instead: one line, the pot, Deal next hand, Host tools.
function WinnerModalCard({ amount, isSplit, onCta, winMessage, winnerNamesJoined, calloutText, canSendCallout, calloutTargets, onSendCallout }) {
  const formatCurrency = useFormatCurrency();
  const counted = useCountUp(amount);

  // Callout picker state — local to this hand's modal (component unmounts when
  // winnerInfo clears, so it resets automatically each hand).
  const [calloutSkipped, setCalloutSkipped] = useState(false);
  const [sendingCallout, setSendingCallout] = useState(false);
  const showCalloutPicker = canSendCallout && !calloutText && !calloutSkipped;

  const handleTargetTap = async (targetId) => {
    setSendingCallout(true);
    try {
      await onSendCallout(targetId);
    } finally {
      setSendingCallout(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-5 bg-ink/45 backdrop-blur-sm"
      style={{ animation: 'cl-win-fade-in 260ms ease' }}
    >
      <ConfettiField />
      <div
        className="relative w-full max-w-sm bg-cream rounded-[22px] p-7 shadow-[0_30px_70px_rgba(0,0,0,0.28)] overflow-hidden"
        style={{ animation: 'cl-win-card-in 380ms cubic-bezier(0.22,1,0.36,1)' }}
      >
        {/* Compact headline (unified) — Table Vibe win line */}
        <WinHeadline message={winMessage} boldPart={winnerNamesJoined} />
        <CalloutLine text={calloutText} />

        {/* Pot count-up */}
        <div className="mt-5 mb-6">
          <div className="label-eyebrow mb-1.5">{isSplit ? t('game.yourShare') : t('game.winnings')}</div>
          <div className="text-[34px] font-medium tracking-[-0.03em] tnum leading-none text-ink">{formatCurrency(counted)}</div>
        </div>

        {/* Callout picker — winner only, single-winner hands, hidden once sent/skipped */}
        {showCalloutPicker && (
          <div className="mb-6">
            <div className="label-eyebrow mb-2">{t('game.sendEmote')}</div>
            <div className="flex flex-wrap gap-1.5">
              {calloutTargets.map(p => (
                <button
                  key={p.id}
                  onClick={() => handleTargetTap(p.id)}
                  disabled={sendingCallout}
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                  className="px-3.5 py-2 rounded-full border border-line bg-transparent text-ink text-[13px] font-medium disabled:opacity-40"
                >
                  {p.name}
                </button>
              ))}
              <button
                onClick={() => setCalloutSkipped(true)}
                disabled={sendingCallout}
                style={{ WebkitTapHighlightColor: 'transparent' }}
                className="px-3.5 py-2 rounded-full border border-line bg-transparent text-mute text-[13px] font-medium disabled:opacity-40"
                data-testid="emote-skip"
              >
                {t('common.skip')}
              </button>
            </div>
          </div>
        )}

        <button
          onClick={onCta}
          style={{ WebkitTapHighlightColor: 'transparent' }}
          className="w-full h-14 rounded-[14px] border-[1.5px] border-ink bg-transparent text-ink text-[15px] font-semibold tracking-[-0.005em] flex items-center justify-center"
          data-testid="winner-got-it"
        >
          {t('common.gotIt')}
        </button>
      </div>
    </div>
  );
}

// Quiet notice — shown to non-host players who didn't win.
function WinnerOpponentNotice({ pot, onCta, winMessage, winnerNamesJoined, calloutText }) {
  const formatCurrency = useFormatCurrency();
  const counted = useCountUp(pot);

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-5 bg-ink/45 backdrop-blur-sm"
      style={{ animation: 'cl-win-fade-in 260ms ease' }}
    >
      <div
        className="relative w-full max-w-sm bg-cream rounded-[22px] p-7 shadow-[0_30px_70px_rgba(0,0,0,0.28)] overflow-hidden"
        style={{ animation: 'cl-win-card-in 380ms cubic-bezier(0.22,1,0.36,1)' }}
      >
        {/* Compact headline — Table Vibe win line */}
        <WinHeadline message={winMessage} boldPart={winnerNamesJoined} />
        <CalloutLine text={calloutText} />

        {/* Pot count-up */}
        <div className="mt-5 mb-6">
          <div className="label-eyebrow mb-1.5">{t('poker.pot')}</div>
          <div className="text-[34px] font-medium tracking-[-0.03em] tnum leading-none" style={{ color: '#5c574e' }}>{formatCurrency(counted)}</div>
        </div>

        <button
          onClick={onCta}
          style={{ WebkitTapHighlightColor: 'transparent' }}
          className="w-full h-14 rounded-[14px] border-[1.5px] border-ink bg-transparent text-ink text-[15px] font-semibold tracking-[-0.005em] flex items-center justify-center"
          data-testid="winner-got-it"
        >
          {t('common.gotIt')}
        </button>
      </div>
    </div>
  );
}

// Shown under a disabled Deal next hand: only one seated player has chips.
function DealNeedsRebuyNote() {
  return (
    <p className="text-[12.5px] text-mute text-center m-0" data-testid="deal-needs-rebuy">
      {t('game.dealNeedsRebuy')}
    </p>
  );
}

function DealWaitingAwayNote() {
  return (
    <p className="text-[12.5px] text-mute text-center m-0" data-testid="deal-needs-away">
      {SIT_OUT_COPY.waitingToDeal}
    </p>
  );
}

// Host-only post-hand rollup. One sentence, the pot, pending phone-rebuy
// rows, Deal next hand, and the same Host tools pill as the table footer.
// canDeal is false while fewer than two seated players have chips (a bust
// heads-up): Deal stays visible but disabled until someone has chips again.
// The old "Rebuy a player in Host tools to deal" hint is gone — pending
// rows take that slot, and a disabled Deal with no extra gap otherwise.
function HostHandResult({
  pot, onDeal, onOpenHostTools, winnerNamesJoined, isSplit, canDeal = true, dealBlockedByAway = false,
  pendingRebuys = [], onAddRebuy, addingRebuyId = null,
  hostChipless = false, hostRequest = null, defaultBuyInLabel = '',
  onRequestRebuy, requestingRebuy = false,
}) {
  const formatCurrency = useFormatCurrency();
  const counted = useCountUp(pot);
  const vv = useVisualViewport();
  const message = isSplit
    ? t('game.winTheHand', { names: winnerNamesJoined })
    : t('game.winsTheHand', { names: winnerNamesJoined });

  return (
    <div
      className="fixed left-0 right-0 z-[60] flex items-center justify-center bg-ink/45 backdrop-blur-sm"
      style={{
        animation: 'cl-win-fade-in 260ms ease',
        top: vv.offsetTop,
        height: vv.height,
        padding: vv.keyboardOpen ? '8px 20px' : '20px',
      }}
      data-testid="host-hand-result"
    >
      <div
        className="relative w-full max-w-sm max-h-full overflow-y-auto bg-cream rounded-[22px] p-7 shadow-[0_30px_70px_rgba(0,0,0,0.28)]"
        style={{ animation: 'cl-win-card-in 380ms cubic-bezier(0.22,1,0.36,1)' }}
      >
        <WinHeadline message={message} boldPart={winnerNamesJoined} />

        <div className="mt-5 mb-6">
          <div className="label-eyebrow mb-1.5">{t('poker.pot')}</div>
          <div className="text-[34px] font-medium tracking-[-0.03em] tnum leading-none text-ink">{formatCurrency(counted)}</div>
        </div>

        <div className="space-y-3">
          {hostChipless && !hostRequest && onRequestRebuy && (
            <RebuyAskForm
              defaultAmount={defaultBuyInLabel}
              onRequest={onRequestRebuy}
              submitting={requestingRebuy}
              showSpectate={false}
            />
          )}
          {pendingRebuys.length > 0 && (
            <PendingRebuyRows requests={pendingRebuys} onAdd={onAddRebuy} addingId={addingRebuyId} />
          )}
          <button
            onClick={onDeal}
            disabled={!canDeal}
            style={{ WebkitTapHighlightColor: 'transparent' }}
            className="w-full h-14 rounded-[14px] bg-ink text-cream text-[15px] font-semibold tracking-[-0.005em] flex items-center justify-center disabled:bg-line disabled:text-mute"
            data-testid="deal-next-hand"
          >
            {t('game.dealNextHand')}
          </button>
          {!canDeal && dealBlockedByAway && <DealWaitingAwayNote />}
          <div className="flex justify-center">
            <button
              type="button"
              onClick={onOpenHostTools}
              data-testid="host-tools-overlay"
              style={{ WebkitTapHighlightColor: 'transparent' }}
              className="bg-transparent text-[12px] font-medium px-3 py-1 rounded-full border border-line text-mute hover:text-ink hover:border-ink transition-colors"
            >
              {t('game.hostTools')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Plain bold ink street-action line. Every table seat and list row
// reserves this slot under the stack so an action appearing cannot
// shift the card. No background, border, or icon. Empty (but still
// reserved) when the street is new.
function SeatBetLine({
  player,
  roundActions = [],
  stage,
  formatCurrency,
  amount = 0,
  size = 'table',
  dark = false,
  reserved = true,
  testId,
  handAwarded = false,
  winnerInfo,
}) {
  const fromAction = player
    ? streetActionLabel(player, { roundActions, stage, formatCurrency, handAwarded, winnerInfo })
    : { text: (amount || 0) > 0 && !handAwarded ? t('street.bet', { amount: formatCurrency(amount) }) : '', mute: false };
  const text = fromAction.text;
  const hasLine = !!text;
  if (!hasLine && !reserved) return null;

  const sizeCls = {
    table: 'text-[10px] min-h-[11px]',
    dense: 'text-[9.5px] min-h-[11px]',
    micro: 'text-[7px] min-h-[11px]',
    list: 'text-[12px] min-h-[14px]',
    compact: 'text-[11px] min-h-[13px]',
  }[size] || 'text-[10px] min-h-[11px]';

  const tone = !hasLine
    ? 'invisible pointer-events-none'
    : fromAction.mute
    ? 'text-[#8a857d]'
    : (dark ? 'text-cream' : 'text-[#0d0d0d]');

  return (
    <span
      data-testid={hasLine ? testId : undefined}
      data-bet-line={hasLine ? '' : undefined}
      aria-hidden={!hasLine}
      className={`${sizeCls} block min-w-0 w-full max-w-full overflow-hidden font-bold tnum leading-none whitespace-nowrap ${tone}`}
    >
      {hasLine ? text : '\u00a0'}
    </span>
  );
}

function SeatBetPill(props) {
  return <SeatBetLine {...props} />;
}

// Player Row Component — cream/ink design
function playerPickProps(onPick, picked) {
  if (!onPick) return {};
  return {
    role: 'button',
    tabIndex: 0,
    'aria-pressed': !!picked,
    'data-pickable': 'true',
    onClick: onPick,
    onKeyDown: (event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onPick();
      }
    },
  };
}

function PlayerCard({ player, isCurrentTurn, isWinner, isHostSeat = false, density = 'default', layout = 'single', inSeatBet = false, roundActions = [], stage, onPick, picked = false, handAwarded = false, winnerInfo }) {
  const formatCurrency = useFormatCurrency();
  const isDealer = player.role === 'DEALER' || player.role === 'DEALER_SMALL_BLIND';
  const isSmallBlind = player.role === 'SMALL_BLIND' || player.role === 'DEALER_SMALL_BLIND';
  const isBigBlind = player.role === 'BIG_BLIND';
  const presence = seatPresence(player);
  const waitingLook = presence === 'away' || presence === 'next';
  const out = presence === 'out';
  const folded = !waitingLook && !out && isFoldedThisHand(player);
  const hatch = folded || out;

  const getRoleText = () => {
    if (out) return null;
    if (presence === 'pending' || presence === 'away' || presence === 'next') {
      return seatStatusLabel(presence, { dense: density === 'dense' || density === 'micro' });
    }
    if (isDealer && isSmallBlind) return t('poker.dSb');
    if (isDealer) return t('poker.d');
    if (isSmallBlind) return t('poker.sb');
    if (isBigBlind) return t('poker.bb');
    return null;
  };

  const dark = !waitingLook && !out && (isCurrentTurn || isWinner || picked);
  const roleText = getRoleText();
  const pickProps = playerPickProps(onPick, picked);

  const containerSize = {
    default: 'rounded-[14px] px-4 py-[14px]',
    compact: 'rounded-[12px] px-3.5 py-2.5',
    dense: layout === 'grid' ? 'rounded-[10px] px-2.5 py-2' : 'rounded-[10px] px-3 py-2',
    micro: '',
  }[density];

  const nameSize = {
    default: 'text-[15.5px]',
    compact: 'text-[14px]',
    dense: 'text-[13px]',
    micro: 'text-[12.5px]',
  }[density];

  const badgeSize = density === 'dense' || density === 'micro'
    ? 'text-[9px] px-[5px] py-[1.5px]'
    : density === 'compact'
    ? 'text-[10px] px-[5px] py-[2px]'
    : 'text-[10.5px] px-[6px] py-[2.5px]';

  const valueSize = density === 'dense' ? 'text-[12px]' : density === 'compact' ? 'text-[13px]' : 'text-[14px]';

  const lineSize = density === 'micro' ? 'micro' : density === 'dense' ? 'dense' : density === 'compact' ? 'compact' : 'list';
  const betLine = (
    <SeatBetLine
      player={player}
      roundActions={roundActions}
      stage={stage}
      formatCurrency={formatCurrency}
      size={lineSize}
      dark={dark}
      reserved
      handAwarded={handAwarded}
      winnerInfo={winnerInfo}
      testId={`bet-chip-${player.name}`}
    />
  );

  const crown = isHostSeat ? <HostCrown /> : null;
  const nameTone = folded
    ? 'font-medium text-mute line-through decoration-mute/70'
    : (waitingLook || out) ? 'font-medium text-mute' : 'font-semibold';

  const renderBadges = () => (
    <>
      {roleText && (
        <span
          data-testid={presence === 'away' ? 'away-badge' : presence === 'pending' ? 'away-pending-badge' : waitingLook ? 'next-hand-badge' : undefined}
          className={`${badgeSize} font-semibold tracking-[0.08em] rounded-[4px] border flex-shrink-0 ${
            dark ? 'border-white/30 text-white/85' : 'border-line text-mute'
          }${folded ? ' opacity-60' : ''}`}
        >
          {roleText}
        </span>
      )}
    </>
  );

  const cardTone = dark
    ? 'bg-ink text-cream border-ink'
    : waitingLook
    ? 'border-dashed border-line bg-cream-2 text-mute'
    : hatch
    ? 'border-dashed border-line-2 text-mute cl-folded-hatch'
    : 'bg-transparent border-line text-ink';

  if (layout === 'grid' && density === 'micro') {
    return (
      <div data-seat-id={player.id} data-testid={`seat-${player.name}`} data-player-id={player.id} {...pickProps} className={`border min-w-0 rounded-[9px] px-2 py-1 transition-colors duration-200 ${onPick ? 'cursor-pointer' : ''} ${cardTone}`}>
        <div className="flex items-center gap-1 min-w-0">
          <span
            data-testid={`seat-name-${player.name}`}
            className={`text-[12.5px] tracking-[-0.01em] truncate leading-none min-w-0 ${
              nameTone
            }`}
          >
            {player.name}
          </span>
          {crown}
          {roleText && (
            <span className={`${badgeSize} font-semibold tracking-[0.08em] rounded-[4px] border flex-shrink-0 ${dark ? 'border-white/30 text-white/85' : 'border-line text-mute'}${folded ? ' opacity-60' : ''}`}>
              {roleText}
            </span>
          )}
          <span className={`ml-auto pl-1 text-[11px] tnum whitespace-nowrap flex-shrink-0 leading-none ${dark ? 'opacity-75' : 'text-mute'}`}>
            {formatCurrency(player.stack)}
          </span>
        </div>
        <div data-testid="seat-bet-row" className="min-h-[11px] mt-0.5">
          {betLine}
        </div>
      </div>
    );
  }

  if (layout === 'grid') {
    return (
      <div data-seat-id={player.id} data-testid={`seat-${player.name}`} data-player-id={player.id} {...pickProps} className={`border min-w-0 transition-colors duration-200 ${onPick ? 'cursor-pointer' : ''} ${containerSize} ${cardTone}`}>
        <div className="flex items-center gap-1 min-w-0 h-[17px]">
          <span
            data-testid={`seat-name-${player.name}`}
            className={`${nameSize} tracking-[-0.01em] truncate leading-tight min-w-0 ${
              nameTone
            }`}
          >
            {player.name}
          </span>
          {crown}
          {renderBadges()}
        </div>
        <div className={`min-w-0 text-[11.5px] tnum leading-tight mt-0.5 whitespace-nowrap ${dark ? 'opacity-75' : 'text-mute'}`}>
          {t('game.stackSuffix', { amount: formatCurrency(player.stack) })}
        </div>
        <div data-testid="seat-bet-row" className="min-h-[12px] mt-0.5">
          {betLine}
        </div>
      </div>
    );
  }

  return (
    <div
      data-seat-id={player.id}
      data-testid={`seat-${player.name}`}
      data-player-id={player.id}
      {...pickProps}
      className={`border transition-colors duration-200 ${onPick ? 'cursor-pointer' : ''} ${containerSize} ${cardTone}`}
    >
      <div className={`flex items-center justify-between min-w-0 ${density === 'dense' ? 'gap-1.5' : density === 'compact' ? 'gap-2' : 'gap-2.5'}`}>
        <div className={`flex items-center min-w-0 ${density === 'dense' ? 'gap-1.5' : density === 'compact' ? 'gap-2' : 'gap-2.5'}`}>
          <span
            data-testid={`seat-name-${player.name}`}
            className={`${nameSize} tracking-[-0.01em] truncate min-w-0 ${
              nameTone
            }`}
          >
            {player.name}
          </span>
          {crown}
          {renderBadges()}
        </div>
        <span
          className={`${density === 'dense' ? 'text-[11.5px]' : valueSize} tnum whitespace-nowrap flex-shrink-0 ${dark ? 'opacity-75' : 'text-mute'}`}
          data-testid={`stack-${player.name}`}
        >
          {formatCurrency(player.stack)}
        </span>
      </div>
      <div data-testid="seat-bet-row" className="min-h-[14px] mt-1">
        {betLine}
      </div>
    </div>
  );
}

// Host Controls Component - Mobile Optimized Remote
// Stage advancement is NOT here — it lives in the Host tools band as a pill
// beside Undo / Move chips (see the host-only band in GameBoard).
function HostControls({
  gameState, onAdvanceStage, onDeclareWinner, canDeclareWinner, isDeclaringWinner,
  tapWinnerMode = false, awardLabel = t('game.award'), awardEnabled = false, onConfirmWinner,
}) {
  const currentStage = gameState.gameState?.stage || 'PRE_FLOP';
  const canAdvanceStage = gameState.gameState?.canAdvanceStage || false;
  const nextStageName = nextStageLabel(gameState.gameState?.nextStageName);

  // When declare winner is available at showdown, it takes priority and blocks other actions
  const mustDeclareWinner = canDeclareWinner && currentStage === 'SHOWDOWN';
  const awardReady = tapWinnerMode && awardEnabled;

  return (
    <div className="space-y-2">
      {/* Primary action: tap-to-award, Declare Winner pop-up, or deal the next
          street. Same reserved h-14 slot as Fold / Call / Raise so the oval
          does not resize when the label changes. */}
      {mustDeclareWinner && tapWinnerMode && (
        <button
          type="button"
          onClick={onConfirmWinner}
          disabled={!awardReady || isDeclaringWinner}
          style={{ WebkitTapHighlightColor: 'transparent' }}
          className="w-full h-14 rounded-[14px] bg-ink text-cream text-[15px] font-semibold tracking-[-0.005em] flex items-center justify-center px-3 disabled:bg-line disabled:text-mute"
          data-testid="btn-award-winner"
        >
          <span className="truncate">{isDeclaringWinner ? t('game.awarding') : awardLabel}</span>
        </button>
      )}

      {mustDeclareWinner && !tapWinnerMode && (
        <button
          onClick={onDeclareWinner}
          style={{ WebkitTapHighlightColor: 'transparent' }}
          className="w-full h-14 rounded-[14px] bg-ink text-cream text-[15px] font-semibold tracking-[-0.005em] flex items-center justify-center gap-2 disabled:opacity-40"
          data-testid="btn-declare-winner"
        >
          <span className="truncate max-w-full px-1">{isDeclaringWinner ? t('game.declaring') : t('game.declareWinnerBtn')}</span>
        </button>
      )}

      {canAdvanceStage && !mustDeclareWinner && (
        <button
          onClick={onAdvanceStage}
          onTouchEnd={(e) => { e.preventDefault(); onAdvanceStage(); }}
          style={{ WebkitTapHighlightColor: 'transparent' }}
          className="w-full h-14 rounded-[14px] bg-ink text-cream text-[15px] font-semibold tracking-[-0.005em] flex items-center justify-center gap-2"
          data-testid="btn-advance-stage"
        >
          {nextStageName} →
        </button>
      )}
    </div>
  );
}

// Player Controls Component — cream/ink design
function PlayerControls({ gameState, currentPlayerId, toCall, potAmount, minRaise, potRaise, allInAmount, raiseAmount, setRaiseAmount, onFold, onCall, onRaise, onAllIn, isPerformingAction, onShowRaisePanel, preAction, checkFoldArmed, onToggleCheckFold }) {
  const formatCurrency = useFormatCurrency();
  const canCheck = toCall === 0;

  if (preAction) {
    // Not your turn but still in the hand: the normal Fold / Check-or-Call /
    // Raise row stays on screen, all dimmed and disabled. A slim Check/Fold
    // pill sits above the middle button (same width) to pre-pick the action;
    // tap again to undo. Distinct test ids so `action-*` still means "your turn".
    const dim = 'h-[60px] min-w-0 rounded-[14px] border-[1.5px] border-line bg-cream-2 text-ink flex flex-col items-center justify-center opacity-40 px-1';
    return (
      <div data-testid="preaction-row">
        <div className="grid grid-cols-3 gap-2 h-[37px] mb-2" data-testid="checkfold-slot">
          <button
            type="button"
            onClick={onToggleCheckFold}
            aria-pressed={!!checkFoldArmed}
            style={{ gridColumn: 2, WebkitTapHighlightColor: 'transparent' }}
            className={`w-full h-[37px] rounded-full text-[13.5px] font-medium whitespace-nowrap flex items-center justify-center transition-colors ${checkFoldArmed ? 'bg-[#e3dfd5] border border-[#d6d1c6] text-[#3a3832]' : 'bg-transparent border border-[#dcd8cf] text-[#6b675f]'}`}
            data-testid="action-checkfold"
          >
            {checkFoldArmed && (
              <svg width="13" height="13" viewBox="0 0 16 16" className="mr-1" aria-hidden="true">
                <circle cx="8" cy="8" r="7.5" fill="#6b675f" />
                <path d="M4.6 8.2l2.2 2.2 4.6-4.8" stroke="#f6f4ef" strokeWidth="1.8" fill="none" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            )}
            {t('poker.checkFold')}
          </button>
        </div>
        <div dir="ltr" className="i18n-ltr grid grid-cols-3 gap-2">
          <button type="button" disabled className={dim} data-testid="preaction-fold">
            <span className="text-[15px] font-semibold leading-none max-w-full truncate px-0.5">{t('poker.fold')}</span>
            <span className="text-[12.5px] leading-none mt-1 h-[13px]" aria-hidden="true" />
          </button>
          <button type="button" disabled className={dim} data-testid="preaction-call">
            <span className="text-[15px] font-semibold leading-none max-w-full truncate px-0.5">{canCheck ? t('poker.check') : t('poker.call')}</span>
            <span className="text-[12.5px] leading-none mt-1 h-[13px] max-w-full tnum truncate">{canCheck ? '' : formatCurrency(toCall)}</span>
          </button>
          <button type="button" disabled className={dim} data-testid="preaction-raise">
            <span className="text-[15px] font-semibold leading-none max-w-full truncate px-0.5">{t('poker.raise')}</span>
            <span className="text-[12.5px] leading-none mt-1 h-[13px]" aria-hidden="true" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* The status banner up top already announces "Your turn", so no eyebrow here. */}

      {/* Reserved Check/Fold pill slot (empty on your turn) so Fold/Call/Raise
          sit at exactly the same height whether or not it is your turn. */}
      <div className="h-[37px] mb-2" aria-hidden="true" data-testid="checkfold-slot" />

      {/* 3-button grid: Fold / Call / Raise — equal widths and one shared button
          style so the row reads as a uniform set. Same fill, same hairline
          border, same internal layout; only the labels differ. */}
      <div dir="ltr" className="i18n-ltr grid grid-cols-3 gap-2">
        <button
          onClick={onFold}
          disabled={isPerformingAction}
          className="h-[60px] rounded-[14px] border-[1.5px] border-line bg-cream-2 text-ink flex flex-col items-center justify-center transition-transform active:scale-[0.97] disabled:opacity-40"
          data-testid="action-fold"
        >
          <span className="text-[15px] font-semibold leading-none max-w-full truncate px-0.5">{t('poker.fold')}</span>
          <span className="text-[12.5px] leading-none mt-1 h-[13px]" aria-hidden="true" />
        </button>
        <button
          onClick={onCall}
          disabled={isPerformingAction}
          className="h-[60px] min-w-0 rounded-[14px] border-[1.5px] border-line bg-cream-2 text-ink flex flex-col items-center justify-center transition-transform active:scale-[0.97] disabled:opacity-40 px-1"
          data-testid="action-call"
        >
          <span className="text-[15px] font-semibold leading-none max-w-full truncate px-0.5">{canCheck ? t('poker.check') : t('poker.call')}</span>
          <span className="text-[12.5px] text-ink leading-none mt-1 h-[13px] max-w-full tnum truncate">{canCheck ? '' : formatCurrency(toCall)}</span>
        </button>
        <button
          onClick={onShowRaisePanel}
          disabled={isPerformingAction}
          className="h-[60px] rounded-[14px] border-[1.5px] border-line bg-cream-2 text-ink flex flex-col items-center justify-center transition-transform active:scale-[0.97] disabled:opacity-40"
          data-testid="action-raise"
        >
          <span className="text-[15px] font-semibold leading-none max-w-full truncate px-0.5">{t('poker.raise')}</span>
          <span className="text-[12.5px] leading-none mt-1 h-[13px]" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}

// Raise Panel — cream/ink centered modal
function RaisePanel({ currentPlayer, minRaise, potAmount, highestBet, bigBlind, onConfirm, onPresetRaise, onClose, isPerformingAction, playersArray, currentPlayerId }) {
  const { formatCurrency, parseCurrencyInput, currency, symbol, amountInputProps } = useCurrency();
  const currentBet = currentPlayer?.currentRoundBet || 0;
  const stack = currentPlayer?.stack || 0;
  const minTotalBet = highestBet + minRaise;
  const maxTotalBet = currentBet + stack;
  const maxEffectiveBet = getMaximumRaise(playersArray, currentPlayerId);
  const actualMaxBet = Math.min(maxTotalBet, maxEffectiveBet);

  const SNAP = getSnapCents(currency);
  const snap = (v) => Math.round(v / SNAP) * SNAP;
  const clamp = (v) => Math.max(minTotalBet, Math.min(actualMaxBet, snap(v)));

  const [amount, setAmount] = useState(() => clamp(highestBet + Math.round(potAmount / 2)));
  // The typed field is a scratchpad, not a mirror of `amount` — it stays blank
  // except while the player is actively typing a number into it.
  const [typed, setTyped] = useState('');
  const typedInputRef = useRef(null);

  // On mobile, the on-screen keyboard overlays a fixed-position modal instead
  // of resizing the layout — the card stays centered on the full-height
  // viewport and the keyboard just covers whatever falls in its band. Track
  // how much of the bottom is covered and pad the wrapper by that amount, so
  // "centered" recenters within the space actually left above the keyboard.
  const [keyboardInset, setKeyboardInset] = useState(0);
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    const handleViewportChange = () => {
      const inset = Math.max(0, document.documentElement.clientHeight - (vv.height + vv.offsetTop));
      setKeyboardInset(inset);
    };
    vv.addEventListener('resize', handleViewportChange);
    vv.addEventListener('scroll', handleViewportChange);
    handleViewportChange();
    return () => {
      vv.removeEventListener('resize', handleViewportChange);
      vv.removeEventListener('scroll', handleViewportChange);
    };
  }, []);

  const commitAmount = (cents) => { setAmount(clamp(cents)); setTyped(''); };
  // Enter key and the inline checkmark both call this, then blur the field —
  // on mobile that dismisses the keyboard so the updated amount (hidden
  // behind it while typing) becomes visible again immediately.
  const parseTypedCents = (raw) => {
    try {
      return parseCurrencyInput(raw);
    } catch {
      return NaN;
    }
  };

  const commitTyped = () => {
    if (typed.trim() === '') return;
    const parsed = parseTypedCents(typed);
    commitAmount(Number.isFinite(parsed) ? parsed : amount);
  };

  const presets = [
    { v: clamp(minTotalBet), sub: t('raise.presetMin'), id: 'MIN' },
    { v: clamp(highestBet + Math.round(potAmount / 2)), sub: t('raise.presetHalf'), id: 'HALF POT' },
    { v: clamp(highestBet + potAmount), sub: t('raise.presetPot'), id: 'POT' },
    { v: actualMaxBet, sub: t('raise.presetAllIn'), id: 'ALL-IN' },
  ].filter((p, i, a) => a.findIndex(q => q.v === p.v) === i);

  const parsedTyped = typed.trim() === '' ? null : parseTypedCents(typed);
  const typedInvalid = typed.trim() !== '' && (!Number.isFinite(parsedTyped) || parsedTyped < minTotalBet || parsedTyped > actualMaxBet);
  const confirmDisabled = isPerformingAction || typedInvalid;

  const pct = actualMaxBet > minTotalBet ? (amount - minTotalBet) / (actualMaxBet - minTotalBet) : 0;
  const fill = `${Math.round(pct * 100)}%`;

  const handleConfirmClick = () => {
    const c = Number.isFinite(parsedTyped) ? clamp(parsedTyped) : amount;
    commitAmount(c);
    if (c >= actualMaxBet) {
      onPresetRaise(actualMaxBet);
    } else {
      onConfirm(c);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-5"
      style={{ animation: 'cl-fade-in 200ms ease', paddingBottom: keyboardInset ? keyboardInset + 20 : undefined, transition: 'padding-bottom 150ms ease' }}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-ink/40 backdrop-blur-[2px]" onClick={onClose} />

      {/* Card */}
      <div className="relative w-full max-w-sm bg-cream rounded-[22px] p-6 shadow-[0_30px_70px_rgba(0,0,0,0.22)] overflow-hidden">
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="label-eyebrow">{t('raise.to')}</div>
            <div className="text-[11.5px] text-mute tnum mt-1">
              {t('raise.minStack', { min: formatCurrency(minTotalBet), stack: formatCurrency(stack) })
                .split('·')
                .map((part, i) => (
                  <React.Fragment key={i}>
                    {i > 0 && <span className="text-line mx-1">·</span>}
                    {part.trim()}
                  </React.Fragment>
                ))}
            </div>
          </div>
          <button onClick={onClose} className="text-mute text-[22px] leading-none p-1 -mt-1 -mr-1 bg-transparent border-none cursor-pointer">×</button>
        </div>

        {/* Amount readout + nudge row */}
        <div className="flex items-center gap-3">
          <button
            onClick={() => commitAmount(amount - bigBlind)}
            disabled={isPerformingAction || amount <= minTotalBet}
            className="w-[54px] h-[54px] shrink-0 rounded-full border-[1.5px] border-line bg-white text-[24px] leading-none text-ink disabled:opacity-30"
          >
            −
          </button>
          <div className="flex-1 min-w-0 text-center">
            <div className="text-[clamp(22px,11vw,44px)] font-medium tracking-[-0.04em] tnum leading-none truncate">{formatCurrency(amount)}</div>
            <div className="text-[12px] text-mute mt-[7px]">
              {amount >= actualMaxBet
                ? t('raise.allInWhole')
                : t('raise.moreThanCall', { amount: formatCurrency(amount - highestBet) })}
            </div>
          </div>
          <button
            onClick={() => commitAmount(amount + bigBlind)}
            disabled={isPerformingAction || amount >= actualMaxBet}
            className="w-[54px] h-[54px] shrink-0 rounded-full border-[1.5px] border-line bg-white text-[24px] leading-none text-ink disabled:opacity-30"
          >
            +
          </button>
        </div>

        {/* Slider */}
        <div className="relative h-11 mt-[18px] mb-0.5">
          <div className="absolute left-0 right-0 top-[17px] h-2.5 rounded-full bg-[#e4e0d8]" />
          <div className="absolute left-0 top-[17px] h-2.5 rounded-full bg-accent-danger" style={{ width: fill }} />
          <div
            className="absolute top-2 w-7 h-7 rounded-full bg-cream border-2 border-ink pointer-events-none"
            style={{ left: `calc(${fill} - 14px)`, boxShadow: '0 2px 6px rgba(0,0,0,0.18)' }}
          />
          <input
            type="range"
            min={minTotalBet}
            max={actualMaxBet}
            step={SNAP}
            value={amount}
            onChange={(e) => commitAmount(Number(e.target.value))}
            disabled={isPerformingAction}
            className="absolute inset-0 w-full h-11 opacity-0 m-0 cursor-pointer"
            aria-label={t('raise.amountAria')}
          />
        </div>
        <div className="flex justify-between text-[11px] text-mute tnum px-0.5">
          <span>{formatCurrency(minTotalBet)}</span>
          <span>{formatCurrency(actualMaxBet)}</span>
        </div>

        {/* Dollar presets — column count tracks how many survive dedup, so a
            collapsed row (e.g. short stack) stays centered instead of leaving
            a gap where the last button would have been. */}
        <div className={`grid ${{ 1: 'grid-cols-1', 2: 'grid-cols-2', 3: 'grid-cols-3' }[presets.length] || 'grid-cols-4'} gap-[7px] my-4`}>
          {presets.map(p => {
            const active = amount === p.v;
            return (
              <button
                key={p.id}
                onClick={() => commitAmount(p.v)}
                disabled={isPerformingAction}
                data-testid={`raise-preset-${p.id.replace(/\s+/g, '-').toLowerCase()}`}
                className={`py-[9px] rounded-[11px] border flex flex-col items-center gap-0.5 disabled:opacity-40 ${
                  active ? 'bg-ink border-ink text-cream' : 'bg-cream-2 border-line text-ink'
                }`}
              >
                <span className="text-[13px] font-semibold tnum truncate max-w-full px-0.5">{formatCurrency(p.v)}</span>
                <span className="text-[9.5px] tracking-[0.04em] opacity-60">{p.sub}</span>
              </button>
            );
          })}
        </div>

        {/* Typed exact amount */}
        <div className="mb-4">
          <div className="label-eyebrow mb-1.5">{t('raise.typeExact')}</div>
          <div className="flex items-center gap-2">
            <div className="flex-1 min-w-0 flex items-baseline tnum border-b border-line">
              <span className="text-[20px] font-medium text-mute leading-none mr-1">{symbol}</span>
              <input
                ref={typedInputRef}
                type="text"
                {...amountInputProps}
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                data-testid="raise-amount-input"
                onKeyDown={(e) => {
                  if (e.key !== 'Enter') return;
                  e.preventDefault();
                  commitTyped();
                  e.currentTarget.blur();
                }}
                onBlur={commitTyped}
                disabled={isPerformingAction}
                className="text-[22px] font-medium tracking-[-0.02em] text-ink leading-none bg-transparent border-none outline-none tnum w-full py-1"
                style={{ MozAppearance: 'textfield' }}
              />
            </div>
            <button
              type="button"
              onClick={() => { commitTyped(); typedInputRef.current?.blur(); }}
              disabled={isPerformingAction || typed.trim() === ''}
              aria-label={t('raise.confirmTyped')}
              className="w-9 h-9 shrink-0 rounded-full border-[1.5px] border-line bg-white text-[16px] leading-none text-ink flex items-center justify-center disabled:opacity-30"
            >
              ✓
            </button>
          </div>
          {typedInvalid && (
            <div className="text-[11.5px] text-accent-danger mt-1.5">
              {Number.isFinite(parsedTyped) && parsedTyped > actualMaxBet
                ? t('raise.onlyHave', { amount: formatCurrency(actualMaxBet) })
                : t('raise.minRaiseIs', { amount: formatCurrency(minTotalBet) })}
            </div>
          )}
        </div>

        {/* Confirm */}
        <button
          onClick={handleConfirmClick}
          disabled={confirmDisabled}
          className="w-full h-14 rounded-full bg-ink text-cream text-[15.5px] font-semibold disabled:opacity-40"
          style={{ WebkitTapHighlightColor: 'transparent' }}
          data-testid="raise-confirm"
        >
          {isPerformingAction ? t('raise.raising') : t('raise.raiseTo', { amount: formatCurrency(amount) })}
        </button>
      </div>
    </div>
  );
}

export {
  highestBet,
  GameStatusBanner,
  HistoryModal,
  winnerInfoDismissKey,
  useCountUp,
  ConfettiField,
  WinHeadline,
  CalloutLine,
  WinnerModalCard,
  WinnerOpponentNotice,
  HostHandResult,
  DealNeedsRebuyNote,
  DealWaitingAwayNote,
  HostCrown,
  SeatBetLine,
  SeatBetPill,
  PlayerCard,
  HostControls,
  PlayerControls,
  RaisePanel,
};
